import React, { useCallback } from "react";
import { omit } from "lodash";
import {
    RuleActionType,
    RuleConditionType,
} from "@common/types/transactionRule";
import { RuleCreationToast } from "@components/transactionRules/RuleCreationToast/RuleCreationToast";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { BulkUpdateTransactionsDto } from "../../../common/dto/transactions/bulk/bulk-update-transactions.dto";
import { useToaster } from "../../general/ToastMessages/useToaster";
import { TransactionsFilters } from "../filters/lib";
import { BulkUpdateParams } from "./lib";

export const BULK_UPDATE_RULE_SUGGESTION_KEY =
    "bulk-update-rule-suggestion-toast";
const BULK_UPDATES_TO_RULE = ["categoryId"];
const SUPPORTED_CONDITIONS = ["counterpartiesIds", "accountIds"];

interface ShowBulkUpdateRuleSuggestionToastParams {
    payloads: BulkUpdateTransactionsDto[];
    params: BulkUpdateParams;
    filtersSelection: TransactionsFilters | null;
}

/**
 * Hook that returns a function to show a toast suggesting rule creation based on bulk update parameters
 */
export function useShowBulkUpdateRuleSuggestionToast() {
    const { toast } = useToaster();
    const { advancedRules } = useTypedFlags();

    return useCallback(
        ({
            payloads,
            params,
            filtersSelection,
        }: ShowBulkUpdateRuleSuggestionToastParams): void => {
            if (!advancedRules) {
                return;
            }

            const payload = payloads[0];

            // We support only category for now
            if (!BULK_UPDATES_TO_RULE.includes(params.update)) {
                return;
            }

            // no apply all is applied, skipping
            if (!payload.options?.filters) {
                return;
            }

            // we need entityId, and one of ( counterpartiesIds, accountIds)
            const filters = payload.options?.filters ?? {};

            // validate default filters, if they are set to something else than default, we don't show the toast
            if (!!filters.requiredAction || !!filters.start || !!filters.end) {
                return;
            }

            const filtersWithoutDefaultFilters = omit(filters, [
                "requiredActions",
                "requiredAction",
                "start",
                "end",
            ]);

            // Check if entityIds is present
            if (!filters.entityIds) {
                return;
            }

            // Check if we have exactly one of the supported filter types (plus entityIds)
            const filterKeys = Object.keys(filtersWithoutDefaultFilters);

            // If there is less than two filters, there is no point in creating a rule, because it is only entityIds,
            // which is a forbidden rule
            if (filterKeys.length < 2) {
                return;
            }

            // Check if the second key is one of our supported filters
            const conditionKeys = filterKeys.find((key) => key !== "entityIds");
            if (
                !conditionKeys ||
                !SUPPORTED_CONDITIONS.includes(conditionKeys)
            ) {
                return;
            }

            // At this point we have entityIds and only supported condition filters

            toast(
                <RuleCreationToast
                    actions={[
                        {
                            actionType: RuleActionType.CATEGORY,
                            value: params.value as string,
                        },
                    ]}
                    conditions={[
                        {
                            conditionType: RuleConditionType.ENTITY,
                            value: filtersSelection?.entityIds as number[],
                        },
                        ...(filtersSelection?.counterpartiesIds
                            ? [
                                  {
                                      conditionType:
                                          RuleConditionType.COUNTERPARTY,
                                      value: filtersSelection?.counterpartiesIds as string[],
                                  },
                              ]
                            : []),
                        ...(filtersSelection?.accountIds
                            ? [
                                  {
                                      conditionType:
                                          RuleConditionType.FINANCIAL_ACCOUNTS,
                                      value: filtersSelection?.accountIds as number[],
                                  },
                              ]
                            : []),
                    ]}
                    applyToExistingTransactions={false}
                    transactionsNumber={0}
                    canCreateWithtoutReview={false}
                />,
                BULK_UPDATE_RULE_SUGGESTION_KEY,
            );
        },
        [toast, advancedRules],
    );
}
